import { Control, Controller, FieldErrors, FieldValues, Path } from 'react-hook-form';
import dayjs from 'dayjs';
import type { FieldDefinition } from '../../../shared/DynamicFormFields';
import {
  NeobetCheckboxField,
  NeobetDateField,
  NeobetPasswordField,
  NeobetSelectField,
  NeobetTextField,
} from '../molecules/NeobetFormFields';

type NeobetDynamicFormFieldsProps<TFieldValues extends FieldValues> = {
  fields: FieldDefinition[];
  control: Control<TFieldValues>;
  errors: FieldErrors<TFieldValues>;
  isSubmitting: boolean;
};

const NeobetDynamicFormFields = <TFieldValues extends FieldValues>({
  fields,
  control,
  errors,
  isSubmitting,
}: NeobetDynamicFormFieldsProps<TFieldValues>) => {
  return (
    <>
      {fields.map((field) => {
        const fieldName = field.name as Path<TFieldValues>;
        const error = errors[fieldName];

        switch (field.type) {
          case 'text':
            return (
              <NeobetTextField
                key={fieldName}
                name={fieldName}
                control={control}
                label={field.label}
                variant='outlined'
                fullWidth
                type='text'
                error={!!error}
                helperText={error?.message as string}
                disabled={isSubmitting || field.disabled}
                placeholder={field.placeholder}
                slotProps={{ htmlInput: { autoComplete: 'off' } }}
                defaultValue={field.defaultValue}
              />
            );
          case 'password':
            return (
              <NeobetPasswordField
                key={fieldName}
                name={fieldName}
                control={control}
                label={field.label}
                error={!!error}
                helperText={error?.message as string}
                disabled={isSubmitting || field.disabled}
                slotProps={{ htmlInput: { autoComplete: 'new-password' } }}
              />
            );
          case 'select':
            return (
              <NeobetSelectField
                key={fieldName}
                name={fieldName}
                control={control}
                label={field.label}
                error={!!error}
                helperText={error?.message as string}
                options={field.options ?? []}
                FormControlProps={{
                  fullWidth: true,
                  disabled: isSubmitting || field.disabled,
                }}
              />
            );
          case 'checkbox':
            return (
              <NeobetCheckboxField
                key={fieldName}
                name={fieldName}
                control={control}
                label={field.label}
                error={!!error}
                helperText={error?.message as string}
                disabled={isSubmitting || field.disabled}
              />
            );
          case 'date':
            return (
              <Controller
                key={fieldName}
                name={fieldName}
                control={control}
                render={({ field: controlledField }) => (
                  <NeobetDateField
                    value={controlledField.value ? dayjs(controlledField.value as string) : null}
                    name={controlledField.name}
                    inputRef={controlledField.ref}
                    label={field.label}
                    error={!!error}
                    helperText={error?.message as string}
                    onChange={(newValue) => controlledField.onChange(newValue?.format('YYYY-MM-DD') ?? '')}
                    disabled={isSubmitting || field.disabled}
                    slotProps={{ field: { onBlur: controlledField.onBlur } }}
                  />
                )}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
};

export default NeobetDynamicFormFields;
